import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { useDeliveryCountdown, type BannerState } from '@/lib/useDeliveryCountdown';
import type { HeroConfig, HeroSlide } from '@/lib/sections/types';
import { CmsSchema, useCmsFields } from '@/cms/content';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';

const DURATION = 6000;
const TRANSITION_DURATION = 900;
const WHITE    = '#ffffff';
const GOLD     = '#e05590';

/* ─────────────────────────────────────────────────────────────
   Hero
───────────────────────────────────────────────────────────── */
export function Hero({ config }: { config: HeroConfig }) {
  const cms = useCmsFields(config.slides.flatMap(slide => [
    { key: `home.hero.${slide.id}.eyebrow`, label: 'Nadtytuł', section: `Baner ${slide.id}`, kind: 'text' as const, defaultValue: slide.eyebrow, required: true, optionalMarker: true },
    { key: `home.hero.${slide.id}.headline`, label: 'Nagłówek', section: `Baner ${slide.id}`, kind: 'textarea' as const, defaultValue: slide.headline, required: true, optionalMarker: true },
    { key: `home.hero.${slide.id}.description`, label: 'Opis', section: `Baner ${slide.id}`, kind: 'textarea' as const, defaultValue: slide.description, required: true, optionalMarker: true },
    { key: `home.hero.${slide.id}.image`, label: 'Zdjęcie', section: `Baner ${slide.id}`, kind: 'image' as const, defaultValue: slide.image, required: true, optionalMarker: true },
    { key: `home.hero.${slide.id}.alt`, label: 'Opis zdjęcia', section: `Baner ${slide.id}`, kind: 'alt' as const, defaultValue: slide.headline.replace(/\n/g, ' '), optionalMarker: true },
    { key: `home.hero.${slide.id}.cta`, label: 'Tekst przycisku', section: `Baner ${slide.id}`, kind: 'text' as const, defaultValue: slide.cta.label, required: true, optionalMarker: true },
  ]));
  const slides = config.slides.map(slide => ({
    ...slide,
    eyebrow: cms.value(`home.hero.${slide.id}.eyebrow`),
    headline: cms.value(`home.hero.${slide.id}.headline`),
    description: cms.value(`home.hero.${slide.id}.description`),
    image: cms.value(`home.hero.${slide.id}.image`),
    imageSrcSet: cms.value(`home.hero.${slide.id}.image`) === slide.image ? slide.imageSrcSet : undefined,
    avifSrcSet: cms.value(`home.hero.${slide.id}.image`) === slide.image ? slide.avifSrcSet : undefined,
    alt: cms.value(`home.hero.${slide.id}.alt`),
    cta: { ...slide.cta, label: cms.value(`home.hero.${slide.id}.cta`) },
  }));
  const [{ current, previous, transition }, setSelection] = useState({ current: 0, previous: null as number | null, transition: 0 });
  const slidesRef = useRef(slides);
  slidesRef.current = slides;
  const selectedIndex = useRef(0);
  const slideRequest = useRef(0);
  const [focusWithin, setFocusWithin] = useState(false);
  const timerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);


  const touchStartX = useRef<number | null>(null);
  const mouseStartX  = useRef<number | null>(null);
  const isDragging   = useRef(false);

  const goTo = useCallback((idx: number) => {
    const requested = ++slideRequest.current;
    const target = slidesRef.current[idx];
    if (!target) return;
    selectedIndex.current = idx;
    // Decode the format already selected by this browser before starting the
    // crossfade, so a slow next photo cannot reveal an empty slide.
    const usesAvif = /\.avif(?:[?#]|$)/i.test(sectionRef.current?.querySelector('img')?.currentSrc ?? '');
    const image = new Image();
    image.sizes = '(max-width: 767px) 40vw, 48vw';
    const srcSet = usesAvif ? target.avifSrcSet ?? target.imageSrcSet : target.imageSrcSet;
    if (srcSet) image.srcset = srcSet;
    image.src = target.image;
    void image.decode().catch(() => {}).then(() => {
      if (slideRequest.current !== requested) return;
      setSelection(state => idx === state.current ? state
        : { current: idx, previous: state.current, transition: state.transition + 1 });
    });
  }, []);

  useEffect(() => () => { slideRequest.current += 1; }, []);
  const next = useCallback(() => {
    goTo((selectedIndex.current + 1) % slides.length);
  }, [goTo, slides.length]);
  const prev = useCallback(() => {
    goTo((selectedIndex.current - 1 + slides.length) % slides.length);
  }, [goTo, slides.length]);

  // Only the current and outgoing slide exist during the CSS crossfade. A
  // rapid selection cancels the earlier cleanup without preloading all slides.
  useEffect(() => {
    if (previous === null) return;
    const id = setTimeout(() => setSelection(state => state.transition === transition
      ? { ...state, previous: null } : state), TRANSITION_DURATION);
    return () => clearTimeout(id);
  }, [previous, transition]);

  useEffect(() => {
    if (focusWithin) return;
    timerRef.current = setTimeout(next, DURATION);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [current, next, focusWithin]);

  const slide = slides[current];
  const previousSlide = previous === null ? undefined : slides[previous];

  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as Element).closest('a, button')) return;
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) delta < 0 ? next() : prev();
    touchStartX.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if ((e.target as Element).closest('a, button')) return;
    mouseStartX.current = e.clientX;
    isDragging.current  = true;
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || mouseStartX.current === null) return;
    if (Math.abs(e.clientX - mouseStartX.current) > 8) {
      if (sectionRef.current) sectionRef.current.style.cursor = 'grabbing';
    }
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging.current || mouseStartX.current === null) return;
    const delta = e.clientX - mouseStartX.current;
    if (Math.abs(delta) > 60) delta < 0 ? next() : prev();
    isDragging.current  = false;
    mouseStartX.current = null;
    if (sectionRef.current) sectionRef.current.style.cursor = '';
  };
  const handleMouseLeave = () => {
    isDragging.current  = false;
    mouseStartX.current = null;
    if (sectionRef.current) sectionRef.current.style.cursor = '';
  };

  return (
    <>
    <header data-testid="home-intro" style={{ maxWidth: 1370, margin: '0 auto', padding: '20px var(--page-gutter) 12px', textAlign: 'center' }}>
      <h1 data-testid="home-heading" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.5rem, 2.8vw, 2.25rem)', fontWeight: 500, lineHeight: 1.15, color: '#1a1a1a', margin: '0 0 10px' }}>
        <CmsEditorialText id="home.heading" value={config.heading} section="Wstęp" label="Główny nagłówek strony" />
      </h1>
      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(12px, 1.1vw, 14px)', color: '#6b6560', lineHeight: 1.65, maxWidth: 800, margin: '0 auto' }}>
        <CmsEditorialText id="home.lead" value={config.lead} section="Wstęp" label="Krótki opis oferty" multiline />
      </p>
    </header>
    <section
      ref={sectionRef}
      data-testid="section-hero"
      className="hero-section w-full"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onFocusCapture={() => setFocusWithin(true)}
      onBlurCapture={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusWithin(false);
      }}
      style={{ overflow: 'hidden', position: 'relative', cursor: 'grab', userSelect: 'none', background: WHITE }}
    >
      <CmsSchema fields={cms.fields} />
      {/* ══════════════════════════════════════
          MOBILE — card slider: text left | photo right + countdown strip below
      ══════════════════════════════════════ */}
      <div
        className="md:hidden"
        data-testid="hero-mobile-slider"
        style={{ padding: '4px clamp(16px, 4vw, 24px) 0', background: '#fff' }}
      >
        {/* Rounded card */}
        <div style={{
          borderRadius: 16,
          overflow: 'hidden',
          background: WHITE,
          position: 'relative',
        }}>
          {/* Text and photo sit alongside each other on white. */}
          <div className="hero-mobile-frame">
            {/* Photo column */}
            {previousSlide && <HeroPhoto key={previousSlide.id} cms={cms} slide={previousSlide} mobile exiting />}
            <HeroPhoto key={slide.id} cms={cms} slide={slide} mobile animated={!!previousSlide} />

            {/* Text layer */}
            <div className="hero-mobile-copy">
              <HeroCopyStack cms={cms} slide={slide} previous={previousSlide} mobile />
            </div>
            <div className="hero-controls-mobile">
              <SliderControls slides={slides} current={current} goTo={goTo} />
            </div>
          </div>

          <MobileCountdownStrip />
        </div>

        {/* spacer so card has breathing room above category tiles */}
        <div style={{ height: 4 }} />
      </div>

      {/* ══════════════════════════════════════
          DESKTOP — photo column
      ══════════════════════════════════════ */}
      <div
        className="hidden md:block"
        style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: '48%', overflow: 'hidden', zIndex: 0 }}
      >
        {previousSlide && <HeroPhoto key={previousSlide.id} cms={cms} slide={previousSlide} exiting />}
        <HeroPhoto key={slide.id} cms={cms} slide={slide} animated={!!previousSlide} />
      </div>

      {/* ══════════════════════════════════════
          DESKTOP — text column
      ══════════════════════════════════════ */}
      <div
        className="hero-desktop-content hidden md:flex"
        style={{
          position: 'relative', zIndex: 2,
          minHeight: 'max(400px, calc(52dvh - 45px))',
          alignItems: 'center',
          paddingTop: 32, paddingBottom: 32,
        }}
      >
        <div style={{ width: '48%', paddingRight: 24 }}>
          <HeroCopyStack cms={cms} slide={slide} previous={previousSlide} />
        </div>
      </div>

      <div className="hero-controls-desktop hidden md:flex">
        <SliderControls slides={slides} current={current} goTo={goTo} />
      </div>

    </section>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   Desktop text — matches reference hierarchy
───────────────────────────────────────────────────────────── */
function HeroPhoto({ cms, slide, mobile = false, exiting = false, animated = false }: {
  cms: ReturnType<typeof useCmsFields>;
  slide: HeroSlide & { alt: string };
  mobile?: boolean;
  exiting?: boolean;
  animated?: boolean;
}) {
  return (
    <picture
      className={`bk-hero-photo${mobile ? ' bk-hero-photo-mobile' : ''}${exiting ? ' bk-hero-exit' : animated ? ' bk-hero-enter' : ''}`}
      aria-hidden={exiting || !mobile ? true : undefined}
      style={{ position: 'absolute', top: 0, right: 0, width: mobile ? '40%' : '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
    >
      {slide.avifSrcSet && <source {...cms.image(`home.hero.${slide.id}.image`)}
        type="image/avif" srcSet={slide.avifSrcSet} sizes="(max-width: 767px) 40vw, 48vw" />}
      <img
        {...cms.image(`home.hero.${slide.id}.image`)}
        {...(mobile ? cms.alt(`home.hero.${slide.id}.alt`) : {})}
        src={slide.image} srcSet={slide.imageSrcSet}
        sizes="(max-width: 767px) 40vw, 48vw" fetchPriority="high" draggable={false}
        alt={mobile && !exiting ? slide.alt : ''} aria-hidden={mobile && !exiting ? undefined : true}
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: slide.imagePosition }}
      />
    </picture>
  );
}

function HeroCopyStack({ cms, slide, previous, mobile = false }: {
  cms: ReturnType<typeof useCmsFields>;
  slide: HeroSlide;
  previous?: HeroSlide;
  mobile?: boolean;
}) {
  return (
    <div className="bk-hero-copy-stack">
      {previous && <HeroText key={previous.id} cms={cms} slide={previous} mobile={mobile} exiting />}
      <HeroText key={slide.id} cms={cms} slide={slide} mobile={mobile} animated={!!previous} />
    </div>
  );
}

function HeroText({ cms, slide, mobile, exiting = false, animated = false }: {
  cms: ReturnType<typeof useCmsFields>;
  slide: HeroSlide;
  mobile: boolean;
  exiting?: boolean;
  animated?: boolean;
}) {
  // The page H1 stays above the slider. The outgoing copy cannot receive
  // focus or be read again while its visual exit finishes.
  return (
    <div
      className={`bk-hero-copy${mobile ? ' bk-hero-copy-mobile' : ''}${exiting ? ' bk-hero-exit' : animated ? ' bk-hero-enter' : ''}`}
      inert={exiting ? true : undefined}
      aria-hidden={exiting ? true : undefined}
    >
          <p
            data-testid="text-hero-headline"
            {...cms.text(`home.hero.${slide.id}.headline`)}
            className={mobile ? 'hero-mobile-headline' : undefined}
            style={mobile ? undefined : {
              fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 'clamp(2rem, 4.2vw, 3.4rem)',
              fontWeight: 400,
              lineHeight: 1.08,
              letterSpacing: '-0.015em',
              color: '#1a1a1a',
              margin: '0 0 clamp(12px, 1.8vh, 22px)',
              whiteSpace: 'pre-line',
            }}
          >
            {slide.headline}
          </p>

          {/* Description */}
          <p
            data-testid="text-hero-description"
            className={mobile ? 'hero-mobile-description' : undefined}
            style={mobile ? undefined : {
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 'clamp(12px, 1.05vw, 14.5px)',
              fontWeight: 300,
              lineHeight: 1.7,
              color: '#6b6560',
              margin: '0 0 clamp(16px, 2.2vh, 28px)',
              whiteSpace: 'pre-line',
            }}
          >
            <span {...cms.text(`home.hero.${slide.id}.description`)}>{slide.description}</span>
          </p>

          {/* CTA */}
          <SlideCta cms={cms} slide={slide} />
    </div>
  );
}

function SlideCta({ cms, slide }: { cms: ReturnType<typeof useCmsFields>; slide: HeroSlide }) {
  return (
    <a href={slide.cta.href} data-testid="button-order-bouquet" className="hero-cta">
      <span {...cms.text(`home.hero.${slide.id}.cta`)}>{slide.cta.label}</span>
      <ArrowRight size={16} strokeWidth={1.5} aria-hidden />
    </a>
  );
}

function SliderControls({ slides, current, goTo }: {
  slides: HeroSlide[];
  current: number;
  goTo: (i: number) => void;
}) {
  return (
    <div className="hero-slider-controls" role="group" aria-label="Sterowanie slajdami">
      <div className="hero-slider-dots">
        {slides.map((slide, i) => (
          <button key={slide.id} type="button" className="hero-slide-selector"
            onClick={() => goTo(i)} aria-label={`Slajd ${i + 1}`}
            aria-current={i === current ? 'true' : undefined}>
            <span aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Mobile countdown strip — white bar below the split card
   Left: "Dostawa dziś? Zamów w ciągu: XX GODZ. XX MIN. XX SEK."
───────────────────────────────────────────────────────────── */
const M_DARK = '#2a1f14';

function MobileTimeTile({ value, unit }: { value: string; unit: string }) {
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0 }}>
      <span style={{
        background: GOLD, color: '#1a1410',
        fontFamily: 'DM Sans, monospace',
        fontSize: 13, fontWeight: 700, lineHeight: 1,
        padding: '4px 0', borderRadius: 4, width: 28,
        textAlign: 'center', fontVariantNumeric: 'tabular-nums',
        display: 'inline-block',
      }}>
        {value}
      </span>
      <span style={{
        fontFamily: 'DM Sans, sans-serif', fontSize: 6.5,
        letterSpacing: '0.07em', color: '#6b6560',
        textTransform: 'uppercase' as const, fontWeight: 600,
      }}>
        {unit}
      </span>
    </span>
  );
}

/**
 * Delivery label and time tiles below the mobile slider controls.
 */
function MobileCountdownStrip() {
  const { bannerState, isCountdown, hh, mm, ss } = useDeliveryCountdown();
  const nextSlot = bannerState as Extract<BannerState, { kind: 'next' }>;

  return (
    <div
      data-testid="hero-mobile-countdown"
      style={{
        background: WHITE,
        borderTop: '1px solid rgba(210,60,120,0.18)',
        padding: '7px 12px 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      {/* Left: label + tiles */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 1, minWidth: 0, flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          color: M_DARK, lineHeight: 1.3, flexShrink: 1, minWidth: 0,
        }}>
          {isCountdown ? (
            <><strong style={{ fontWeight: 600 }}>Dostawa dziś?</strong>{' '}Zamów w ciągu:</>
          ) : (
            <>Dostawa od{' '}
              <strong style={{ fontWeight: 600, color: '#b83f74' }}>
                {nextSlot.dateLabel} {nextSlot.startHour}
              </strong>
            </>
          )}
        </span>

        {isCountdown && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, flexShrink: 0 }}>
            <MobileTimeTile value={hh} unit="GODZ." />
            <MobileTimeTile value={mm} unit="MIN." />
            <MobileTimeTile value={ss} unit="SEK." />
          </div>
        )}
      </div>

    </div>
  );
}
